import type React from "react";
import { useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import type {
  DifferentialColorScheme,
  DifferentialHeatmapResult,
} from "../../types/gazeHeatmapTypes";
import { generateDifferentialLegendImage } from "../../utils/differentialHeatmapGeneration";

interface DifferentialHeatmapLegendProps {
  /** Color scheme used */
  colorScheme: DifferentialColorScheme;
  /** Name/label for Group A */
  groupAName: string;
  /** Name/label for Group B */
  groupBName: string;
  /** Optional result with statistics */
  result?: DifferentialHeatmapResult | null;
  /** Additional CSS classes */
  className?: string;
}

/**
 * Legend component for differential heatmaps
 * Shows a divergent color scale with group labels
 */
const DifferentialHeatmapLegend: React.FC<DifferentialHeatmapLegendProps> = ({
  colorScheme,
  groupAName,
  groupBName,
  result,
  className = "",
}) => {
  const { t } = useTranslation();
  const canvasRef = useRef<HTMLCanvasElement>(null);

  // Render legend gradient
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const legendImage = generateDifferentialLegendImage(colorScheme, 200, 16);
    canvas.width = legendImage.width;
    canvas.height = legendImage.height;
    ctx.putImageData(legendImage, 0, 0);
  }, [colorScheme]);

  return (
    <div className={`flex flex-col gap-2 ${className}`}>
      {/* Main legend row */}
      <div className="flex items-center gap-3 text-sm">
        {/* Group B label (left side = blue = Group B dominates) */}
        <div className="flex items-center gap-1 min-w-[80px] justify-end">
          <span className="text-blue-600 font-medium truncate max-w-[70px]" title={groupBName}>
            {groupBName}
          </span>
          <span className="text-gray-400">{">"}</span>
        </div>

        {/* Color gradient */}
        <canvas
          ref={canvasRef}
          className="h-4 rounded border border-gray-200"
          style={{ width: "140px", height: "14px" }}
        />

        {/* Group A label (right side = red = Group A dominates) */}
        <div className="flex items-center gap-1 min-w-[80px]">
          <span className="text-gray-400">{"<"}</span>
          <span className="text-red-600 font-medium truncate max-w-[70px]" title={groupAName}>
            {groupAName}
          </span>
        </div>
      </div>

      {/* Stats row (optional) */}
      {result && (
        <div className="flex items-center justify-center gap-4 text-xs text-gray-500">
          <span>
            {groupAName}:{" "}
            <span className="font-medium">{result.groupAStats.pointCount.toLocaleString()}</span>{" "}
            {t("statsNew.gazeHeatmap.points")}
          </span>
          <div className="h-3 w-px bg-gray-300" />
          <span>
            {groupBName}:{" "}
            <span className="font-medium">{result.groupBStats.pointCount.toLocaleString()}</span>{" "}
            {t("statsNew.gazeHeatmap.points")}
          </span>
        </div>
      )}
    </div>
  );
};

export default DifferentialHeatmapLegend;
